import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import LazyAnimation from './LazyAnimation';

const useStyles = makeStyles(theme => ({
  wrapper: {
    paddingTop: 58,
    paddingBottom: 63,
    minHeight: 901,
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },
  parallaxBg: {
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundImage:
      'url(https://solutions.crediblemind.com/wp-content/uploads/2019/12/crowd.png)',
    backgroundSize: 'cover',
    backgroundAttachment: 'fixed',
  },
  container: {
    width: '80%',
    maxWidth: 1080,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    '& > p': {
      zIndex: 1,
    },
  },
  header: {
    fontFamily: 'PoppinsSemiBold',
    fontWeight: '600',
    fontSize: 30,
    lineHeight: '1.4em',
    textAlign: 'center',
    paddingBottom: 67,
    color: '#FFFFFF',
  },
  body: {
    maxWidth: '80%',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontSize: 18,
    lineHeight: '1.8em',
    paddingBottom: '1em',
    fontFamily: 'MontserratMedium',
    fontWeight: '500',
    color: '#FFFFFF',
  },
  optionsWrapper: {
    marginTop: 40,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  optionContainer: {
    width: '30%',
    textAlign: 'center',
    zIndex: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: 30,
    },
  },
  buttonRoot: {
    color: '#FFFFFF',
    borderRadius: 4,
    letterSpacing: 2,
    fontSize: 15,
    fontFamily: 'MontserratSemiBold',
    fontWeight: '600',
    textTransform: 'uppercase',
    backgroundColor: '#0C71C3',
    marginBottom: '9%',
    padding: '12px 1em',
    transition: 'all 300ms ease 0ms',
    position: 'relative',
    boxShadow: '0px 2px 18px 0px rgba(0,0,0,0.3)',
    '& > svg': {
      position: 'absolute',
      opacity: 0,
      marginLeft: '-1em',
      transition: 'all 300ms ease 0ms',
      right: 4,
    },
    '&:hover': {
      paddingRight: '2em',
      paddingLeft: '0.7em',
      borderRadius: 25,
      '& > svg': {
        opacity: 1,
        marginLeft: 0,
      },
    },
  },
  optionText: {
    color: '#FFFFFF',
    fontFamily: 'OpenSansBold',
    fontSize: 14,
    lineHeight: '1.7em',
    fontWeight: '700',
    textAlign: 'center',
  },
  optionNew: {
    fontSize: 20,
    fontFamily: 'OpenSansBold',
    lineHeight: '1em',
    color: '#E09900',
    fontWeight: '700',
    textAlign: 'center',
  },
  divider: {
    backgroundImage:
      'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSI2MHB4IiB2aWV3Qm94PSIwIDAgMTI4MCAxNDAiIHByZXNlcnZlQXNwZWN0UmF0aW89Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0iI2Y3ZjdmNyI+PHBhdGggZD0iTTAgMHYxNDBoMTI4MEwwIDB6Ii8+PC9nPjwvc3ZnPg==)',
    backgroundSize: '100% 60px',
    bottom: 0,
    right: 0,
    left: 0,
    position: 'absolute',
    height: 60,
    zIndex: 1,
  },
}));

function ParallaxA({ onPageExit }) {
  const classes = useStyles();

  const options = [
    {
      label: 'On-demand replay',
      link: 'https://solutions.crediblemind.com/provider-resilience/',
      text: 'COVID-19 and Provider Resilience',
      new: true,
    },
    {
      label: 'On-demand replay',
      link:
        'https://solutions.crediblemind.com/flattening-the-second-curve-of-covid-19/',
      text: 'Flattening the Second Curve of COVID-19: Mental Health',
      new: false,
    },
    {
      label: 'Watch the videos',
      link: 'https://solutions.crediblemind.com/videos',
      text:
        'Short Overviews of Population-based Mental Health Concepts and Benefits',
      new: false,
    },
  ];

  return (
    <section id="solutions-section">
      <div className={classes.wrapper}>
        <div className={classes.parallaxBg} />
        <div className={classes.container}>
          <LazyAnimation type="translate-bottom-20">
            <Typography className={classes.header}>
              Introducing an Early Intervention and Prevention System Supporting
              Population-Based Mental Health
            </Typography>
          </LazyAnimation>
          <LazyAnimation type="translate-bottom-80">
            <Typography className={classes.body}>
              For years we’ve seen Population Health as the systematic approach
              to health care that aims to prevent and cure disease by keeping
              people healthy. But the elusive piece of the puzzle has always
              been mental health. How do you engage large-scale populations in
              an accessible and practical way?
            </Typography>
            <Typography className={classes.body}>
              With CredibleMind you’ll discover a prevention and
              early-intervention solution that enables you to work on
              population-based strategies. We’re driven to de-stigmatize seeking
              help, to make self-help evidence-based, and to connect people to
              credible resources matched to their needs and preferences. Let’s
              work together to transform how people improve their mental,
              emotional and spiritual wellbeing.
            </Typography>
          </LazyAnimation>
          <div className={classes.optionsWrapper}>
            {options.map(option => (
              <div className={classes.optionContainer} key={option.link}>
                <LazyAnimation type="fade-in">
                  <ButtonBase
                    component="a"
                    classes={{ root: classes.buttonRoot }}
                    disableRipple
                    href={option.link}
                    target="_blank"
                    onClick={() => onPageExit(option.link, 'solutions-section')}
                  >
                    {option.label}
                    <ChevronRightIcon />
                  </ButtonBase>
                </LazyAnimation>
                <LazyAnimation type="scale-up-0 fade-in">
                  <Typography className={classes.optionText}>
                    {option.text}
                  </Typography>
                  {option.new && (
                    <Typography className={classes.optionNew}>NEW!</Typography>
                  )}
                </LazyAnimation>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.divider} />
      </div>
    </section>
  );
}

ParallaxA.propTypes = {
  onPageExit: PropTypes.func,
};

export default ParallaxA;
